"use client";

import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react';
import { useUnit } from './UnitContext'
import { UnitContext } from '@/lib/types';
import { getDictionarySync, i18nLoader, i18nKeys } from '@/lib/i18n/getDictionary';

type Locale = 'pt-BR' | 'en-US' | 'es-ES';

interface Translations {
  common: any;
  units: any;
  assets: any;
  settings: any;
  billing: any;
}

interface TranslationContextType {
  locale: Locale;
  unitContext: UnitContext;
  translations: Translations;
  t: (key: string, params?: Record<string, string>) => string;
  tu: (key: string, params?: Record<string, string>) => string;
  ta: (group: string, key: string, params?: Record<string, string>) => string;
  setLocale: (locale: Locale) => void;
}

const defaultLocale: Locale = (import.meta.env.VITE_DEFAULT_LOCALE as Locale) || 'pt-BR';

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const deepMerge = (target: any, source: any) => {
  for (const key of Object.keys(source || {})) {
    const srcVal = source[key];
    const tgtVal = target[key];
    if (Array.isArray(srcVal)) {
      target[key] = srcVal.slice();
    } else if (srcVal && typeof srcVal === 'object') {
      if (!tgtVal || typeof tgtVal !== 'object') target[key] = {};
      deepMerge(target[key], srcVal);
    } else {
      target[key] = srcVal;
    }
  }
};

const getInitialLocale = (): Locale => {
  if (typeof window !== 'undefined') {
    const storedLocale = localStorage.getItem('preferredLocale') as Locale;
    if (storedLocale && ['pt-BR', 'en-US', 'es-ES'].includes(storedLocale)) {
      return storedLocale;
    }
  }
  return defaultLocale;
};

/**
 * Load all translation namespaces synchronously.
 * In Vite all JSON files are eagerly bundled via import.meta.glob so
 * i18nLoader is purely synchronous — no async tick, no FOUC.
 */
function buildTranslationsSync(locale: Locale, unitContext: string): Translations {
  // --- common + context override ---
  const common: any = {};
  const baseCommonPath = `./${locale}/common.json`;
  if (i18nLoader) {
    const baseCommon = i18nLoader(baseCommonPath);
    if (baseCommon) deepMerge(common, baseCommon.default ?? baseCommon);
  }
  if (unitContext) {
    const ctxPath = `./${locale}/${unitContext}.json`;
    if (i18nKeys.includes(ctxPath) && i18nLoader) {
      const ctxData = i18nLoader(ctxPath);
      if (ctxData) deepMerge(common, ctxData.default ?? ctxData);
    }
  }

  // --- dashboard merged into common ---
  const dashboard = getDictionarySync(locale, 'dashboard');
  if (dashboard && Object.keys(dashboard).length > 0) {
    deepMerge(common, { dashboard });
  }

  // --- onboarding merged into common ---
  const onboarding = getDictionarySync(locale, 'onboarding');
  if (onboarding && Object.keys(onboarding).length > 0) {
    deepMerge(common, { onboarding });
  }

  const units    = getDictionarySync(locale, 'units');
  const assets   = getDictionarySync(locale, 'assets');
  const settings = getDictionarySync(locale, 'settings');
  const billing  = getDictionarySync(locale, 'billing');

  return { common, units, assets, settings, billing };
}

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);
  
  // Read unit context from UnitProvider - this is a reactive hook that will cause re-render
  // Support both Vite env var and legacy NEXT_PUBLIC_ prefix as a fallback
  const defaultContext = (
    (import.meta.env.VITE_PUBLIC_APP_CONTEXT as UnitContext) ||
    (import.meta.env.NEXT_PUBLIC_APP_CONTEXT as UnitContext) ||
    'farm'
  );
  
  // Get reactive context from UnitProvider - call hook unconditionally
  let unitCtx = null;
  try {
    unitCtx = useUnit();
  } catch (e) {
    // useUnit may throw if UnitProvider is not mounted
  }
  
  const unitContext: UnitContext = (unitCtx?.contextKey || defaultContext) as UnitContext;

  // Computed synchronously during every render — translations always match locale/unitContext
  // with no extra render cycle, eliminating the flash-of-untranslated-content.
  const currentTranslations = useMemo(
    () => buildTranslationsSync(locale, unitContext),
    [locale, unitContext]
  );

  // Função para alterar o locale e salvar no localStorage
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferredLocale', newLocale);
    }
  };

  // Função para buscar tradução por caminho (ex: "app.name", "actions.create")
  const t = (key: string, params?: Record<string, string>): string => {
    const keys = key.split('.');
    
    // Helper function to traverse object by keys
    const findValue = (obj: any, keyPath: string[]): any => {
      let value = obj;
      for (const k of keyPath) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return undefined;
        }
      }
      return value;
    };

    // Try to find in common first
    let value = findValue(currentTranslations.common, keys);
    
    // If not found in common, try in units
    if (value === undefined) {
      value = findValue(currentTranslations.units, keys);
    }
    
    // If not found in units, try in assets
    if (value === undefined) {
      // Support keys prefixed with `assets.` (e.g. 'assets.bovines.title')
      // by attempting both the full path and the path without the leading 'assets'.
      value = findValue(currentTranslations.assets, keys);
      if (value === undefined && keys[0] === 'assets') {
        value = findValue(currentTranslations.assets, keys.slice(1));
        // If still not found, try default fallback under assets.default
        if (value === undefined && keys.length >= 3) {
          const fallback = findValue(currentTranslations.assets, ['default', ...keys.slice(2)]);
          if (fallback !== undefined) value = fallback;
        }
      }
    }
    
    // If key starts with 'units.', also try flat lookup in the units dict
    // This allows t('units.admin') to resolve units['admin'] from units/common.json
    if (value === undefined && keys[0] === 'units' && keys.length > 1) {
      value = findValue(currentTranslations.units, keys.slice(1));
    }

    // If key starts with 'settings.', try in settings namespace
    // This allows t('settings.assets.categories.list.title') to resolve from settings namespace
    if (value === undefined && keys[0] === 'settings' && keys.length > 1) {
      value = findValue(currentTranslations.settings, keys.slice(1));
    }
    
    // Also try without the 'settings.' prefix for backwards compatibility
    if (value === undefined) {
      value = findValue(currentTranslations.settings, keys);
    }

    // If key starts with 'billing.', try in billing namespace
    if (value === undefined && keys[0] === 'billing' && keys.length > 1) {
      value = findValue(currentTranslations.billing, keys.slice(1));
    }

    // If still not found, return the key itself
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }

    let result = typeof value === 'string' ? value : key;

    // Replace placeholders like {{unit}} with params
    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([param, replacement]) => {
        result = result.replace(new RegExp(`{{${param}}}`, 'g'), String(replacement));
      });
    }

    return result;
  };

  // Traverses an object by a dot-separated key path (e.g. 'list.title' or 'singular')
  const traverseKeys = (obj: any, keyPath: string): any => {
    const parts = keyPath.split('.');
    let value = obj;
    for (const k of parts) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }
    return value;
  };

  // Função para buscar tradução de unidade automaticamente com suporte a dot-notation.
  // Ordem: units[contextKey].key → units.key (fallback para common.json)
  // Exemplo: tu('list.title') → units.farm.list.title → units.list.title
  //          tu('singular')  → units.farm.singular   → units.singular
  const tu = (key: string, params?: Record<string, string>): string => {
    // 1. Try context-specific (e.g. units.farm.list.title)
    let value = traverseKeys(currentTranslations.units?.[unitContext as UnitContext], key);
    // 2. Fall back to common (e.g. units.list.title  or  units.singular)
    if (value === undefined) {
      value = traverseKeys(currentTranslations.units, key);
    }
    if (value === undefined) {
      console.warn(`Unit translation key not found: ${unitContext}.${key}`);
      return key;
    }
    let result = typeof value === 'string' ? value : key;
    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([param, replacement]) => {
        result = result.replace(new RegExp(`{{${param}}}`, 'g'), String(replacement));
      });
    }
    return result;
  };

  // Função para buscar tradução de asset por grupo e chave (com dot notation)
  // Similar to tu() but for assets with explicit group parameter
  // The getDictionary already merges assets/common.json with assets/{group}/common.json
  const ta = (group: string, key: string, params?: Record<string, string>): string => {
    // Try to find in assets[group] using dot notation traversal
    // This already has fallbacks merged from assets/common.json by getDictionary
    let value = traverseKeys(currentTranslations.assets?.[group], key);
    
    // Fallback to legacy flat structure for backwards compatibility
    if (value === undefined && currentTranslations.assets?.groups?.[group]) {
      value = currentTranslations.assets.groups[group][key];
    }
    
    // If group doesn't exist or key not found, fallback to base assets namespace
    // This allows new asset types (e.g. caprines, equines) to work without specific files
    if (value === undefined) {
      // Try direct lookup in assets namespace (from assets/common.json)
      value = traverseKeys(currentTranslations.assets, key);
    }
    
    if (value === undefined) {
      console.warn(`Asset translation key not found: ${group}.${key}`);
      return key;
    }
    
    let result = typeof value === 'string' ? value : key;

    // Replace {{placeholders}} with params
    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(paramValue));
      });
    }

    return result;
  };

  // Block rendering until unit context is resolved so children never see
  // stale / default-context translations before the real context is known.
  if (unitCtx?.isLoading) {
    // Show only the translated loading label while unit context resolves.
    const loadingLabel = (currentTranslations?.common as any)?.loading ?? 'Loading...';

    // Respect user's saved theme (before ThemeProvider mounts)
    let savedTheme: 'dark' | 'light' | null = null
    if (typeof window !== 'undefined') {
      const s = localStorage.getItem('theme')
      if (s === 'dark' || s === 'light') savedTheme = s
    }

    const wrapperClass = savedTheme === 'dark'
      ? 'min-h-screen flex items-center justify-center bg-slate-900'
      : 'min-h-screen flex items-center justify-center bg-white'

    const textClass = savedTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'

    return (
      <div className={wrapperClass}>
        <div className={`text-sm ${textClass}`}>{loadingLabel}</div>
      </div>
    )
  }

  return (
    <TranslationContext.Provider
      value={{
        locale,
        unitContext,
        translations: currentTranslations,
        t,
        tu,
        ta,
        setLocale,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
}

// Hook simplificado apenas para traduções de unidade
export function useUnitTranslation() {
  const { tu, unitContext } = useTranslation();
  return { tu, unitContext };
}

// Hook simplificado apenas para traduções de assets
export function useAssetTranslation(group?: string): {
  ta: (key: string, params?: Record<string, string>) => string;
  group?: string;
} {
  const { ta } = useTranslation();
  
  // If group is provided, return a pre-configured ta for that group
  if (group) {
    const boundTa = (key: string, params?: Record<string, string>): string => ta(group, key, params);
    return { ta: boundTa, group };
  }
  
  // Otherwise return the full ta function (but this shouldn't be used without a group)
  const unboundTa = (key: string, params?: Record<string, string>): string => {
    throw new Error('useAssetTranslation: group is required. Please provide an asset group.');
  };
  return { ta: unboundTa };
}
